import { Box, Card, Flex, HStack, Image, Text } from "@chakra-ui/react";
import { observer } from "mobx-react";
import {
  AttributeDefinitions,
  AttributeName,
  AttributeSelect,
  PresenceType,
} from "@keita/lanepatrol-sdk";
import { AttributeUtils } from "../../../../../../coding/utils/attribute-utils";
import { useStores } from "../../../../../../shared/stores/RootStore";
import { useAttributeHighlight } from "../../hooks/useAttributeHighlight";
import { AttributeWrapper } from "./AttributeWrapper";
import { DotIcon } from "../../../../../../shared/views/icons";
import { OsaNodeCoding } from "../../../../../../coding/models/OsaNodeCoding";
import { attributeImageMap } from "../../../../../../coding/utils/attribute-images";

enum Option {
  NotPresent = "NotPresent",
  Present0to1 = "Present0to1",
  Present1to3 = "Present1to3",
}

const getImage = (subName: string, option: Option): string => {
  switch (option) {
    case Option.NotPresent:
      return attributeImageMap[
        `adjacentFeatures0to1m.${subName}` as AttributeName
      ][PresenceType.NotPresent];
    case Option.Present0to1:
      return attributeImageMap[
        `adjacentFeatures0to1m.${subName}` as AttributeName
      ][PresenceType.Present];
    case Option.Present1to3:
      return attributeImageMap[
        `adjacentFeatures1to3m.${subName}` as AttributeName
      ][PresenceType.Present];
  }
};

const options = Object.values(Option);

const AdjacentFeaturesAttributeBase = ({
  subName,
  nodeCoding,
  disabled,
  optional,
  compact,
}: {
  disabled: boolean;
  subName: string;
  optional: boolean;
  compact: boolean;
  nodeCoding: OsaNodeCoding;
}) => {
  const { codingStore } = useStores();

  const mainName = "adjacentFeatures";
  const name0to1m = `${mainName}0to1m.${subName}` as AttributeName;
  const name1to3m = `${mainName}1to3m.${subName}` as AttributeName;

  const definition0to1m = AttributeDefinitions.getByName(
    name0to1m,
  ) as AttributeSelect;
  const definition1to3m = AttributeDefinitions.getByName(
    name1to3m,
  ) as AttributeSelect;

  const highlighted0to1m = useAttributeHighlight(definition0to1m);
  const highlighted1to3m = useAttributeHighlight(definition1to3m);
  const highlighted = highlighted0to1m && highlighted1to3m;

  if (disabled) return null;

  const isSuggested =
    nodeCoding.suggestions.includes(name0to1m) ||
    nodeCoding.suggestions.includes(name1to3m);

  const { adjacentFeatures0to1m, adjacentFeatures1to3m } =
    nodeCoding.attributes as any;
  const value0to1m = adjacentFeatures0to1m[subName] as PresenceType | null;
  const value1to3m = adjacentFeatures1to3m[subName] as PresenceType | null;

  const selectedOption = mergePresenceTypes(value0to1m, value1to3m);

  const invalid = value0to1m == null || value1to3m == null;

  const onSelect = (option: Option, ctrlKey: boolean) => {
    const deselect = option === selectedOption || ctrlKey;

    const [value0to1m, value1to3m] = splitPresenceTypes(
      deselect ? null : option,
    );

    codingStore.codeAttribute(name0to1m, value0to1m);
    codingStore.codeAttribute(name1to3m, value1to3m);
  };

  const getColor = (isSelected: boolean) =>
    isSelected && isSuggested
      ? `gray.${highlighted ? 300 : 200}`
      : isSelected
      ? `primary.${highlighted ? 300 : 100}`
      : "transparent";

  return (
    <AttributeWrapper
      label={AttributeUtils.getLabel(`${mainName}.${subName}` as AttributeName)}
      attributeDefinition={definition0to1m}
      isSuggested={isSuggested}
      compact={compact}
    >
      <Card.Root
        variant="outline"
        borderColor={invalid && !optional ? "red.400" : undefined}
      >
        {compact ? (
          <HStack flexWrap="wrap" gap={0} separator={<DotIcon />}>
            {options.map((option) => {
              const isSelected = option === selectedOption;

              return (
                <Box
                  p={1}
                  borderRadius="4px"
                  key={option}
                  transitionDuration="0.2s"
                  bgColor={
                    isSelected && isSuggested
                      ? `gray.${highlighted ? 300 : 200}`
                      : isSelected
                      ? `primary.${highlighted ? 300 : 100}`
                      : "transparent"
                  }
                  cursor="pointer"
                  onClick={(e) => onSelect(option, e.ctrlKey || e.metaKey)}
                >
                  <Text fontSize={"xs"}>
                    {AttributeUtils.getOptionLabel(option)}
                  </Text>
                </Box>
              );
            })}
          </HStack>
        ) : (
          <HStack flexWrap="wrap" flexGrow={1} justify="center" gap={0}>
            {options?.map((option) => {
              const isSelected = option === selectedOption;
              return (
                <Flex
                  overflow="hidden"
                  flexShrink={0}
                  borderRadius="4px"
                  w={"80px"}
                  maxW="80px"
                  justifyContent="center"
                  key={option}
                  transitionDuration="0.2s"
                  bgColor={getColor(isSelected)}
                  cursor={"pointer"}
                  onClick={(e) => onSelect(option, e.ctrlKey || e.metaKey)}
                >
                  <Box m={-2}>
                    <Image
                      title={AttributeUtils.getOptionLabel(option)}
                      src={getImage(subName, option)}
                    />
                  </Box>
                </Flex>
              );
            })}
          </HStack>
        )}
      </Card.Root>
    </AttributeWrapper>
  );
};
export const AdjacentFeaturesAttribute = observer(
  AdjacentFeaturesAttributeBase,
);

const mergePresenceTypes = (
  value0to1m: PresenceType | null,
  value1to3m: PresenceType | null,
): Option | null => {
  if (value0to1m === PresenceType.Present) return Option.Present0to1;
  if (value1to3m === PresenceType.Present) return Option.Present1to3;
  if (value0to1m && value1to3m) return Option.NotPresent;
  return null;
};

const splitPresenceTypes = (option: Option | null): (PresenceType | null)[] => {
  switch (option) {
    case Option.NotPresent:
      return [PresenceType.NotPresent, PresenceType.NotPresent];
    case Option.Present0to1:
      return [PresenceType.Present, PresenceType.NotPresent];
    case Option.Present1to3:
      return [PresenceType.NotPresent, PresenceType.Present];
    case null:
      return [null, null];
  }
};
